import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Award, ExternalLink, Share2, Clock, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SharedCertificatePage() {
  const { certId } = useParams();
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchCert = async () => {
      try {
        const res = await axios.get(`${API}/certificates/${certId}`);
        setCert(res.data);
      } catch {
        setError(true);
      }
      setLoading(false);
    };
    fetchCert();
  }, [certId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !cert) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="cert-not-found">
        <div className="text-center">
          <Award className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-700 mb-2">Certificate not found</h2>
          <p className="text-slate-500">This certificate may have been removed or the link is invalid.</p>
          <Button className="mt-4 bg-blue-600 text-white" onClick={() => window.location.href = '/'} data-testid="go-home-btn">
            Go to IA Academy
          </Button>
        </div>
      </div>
    );
  }

  const levelColors = { beginner: '#10B981', intermediate: '#F59E0B', advanced: '#EF4444' };
  const formattedDate = cert.issued_at ? new Date(cert.issued_at).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric'
  }) : '';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="shared-certificate-page">
      <SEO
        title={cert ? `Certificat ${cert.program_title || 'IA Academy'}` : 'Certificat IA Academy'}
        description={cert
          ? `Certificat IA Academy obtenu par ${cert.user_name || 'un apprenant'} pour le programme ${cert.program_title || ''}.`
          : "Vérifiez l'authenticité d'un certificat IA Academy."}
        path={`/certificate/${certId}`}
        type="article"
      />
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="https://customer-assets.emergentagent.com/job_2071b760-1de1-4ab9-81d8-8ca2ec59ffcc/artifacts/lq8u7cy5_LOGO%20IA.png" alt="IA Academy" className="w-8 h-8 rounded-lg" />
            <span className="font-bold text-slate-900" style={{ fontFamily: 'Poppins' }}>IA Academy</span>
          </div>
          <Button size="sm" className="bg-blue-600 text-white gap-1.5" onClick={() => window.location.href = '/'} data-testid="join-btn">
            Join IA Academy <ExternalLink className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Certificate */}
        <div className="relative bg-white border-2 border-blue-100 rounded-2xl overflow-hidden shadow-xl shadow-blue-50" data-testid="shared-cert-card">
          {/* Background */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%234F46E5' fill-opacity='1'%3E%3Cpath d='M50 50c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10s-10-4.477-10-10 4.477-10 10-10zM10 10c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10S0 25.523 0 20s4.477-10 10-10z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
          <div className="h-2 bg-gradient-to-r from-blue-600 via-orange-500 to-blue-600" />

          <div className="relative p-12 md:p-16 text-center">
            <div className="flex items-center justify-center gap-2 mb-6">
              <img src="https://customer-assets.emergentagent.com/job_2071b760-1de1-4ab9-81d8-8ca2ec59ffcc/artifacts/lq8u7cy5_LOGO%20IA.png" alt="IA Academy" className="w-12 h-12 rounded-xl" />
              <span className="text-2xl font-bold text-blue-600" style={{ fontFamily: 'Poppins' }}>IA Academy</span>
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8" style={{ fontFamily: 'Poppins' }}>
              TRAINING CERTIFICATE
            </h1>

            <p className="text-sm text-slate-500 mb-2">Awarded to</p>
            <h2 className="text-3xl md:text-5xl font-bold text-blue-700 mb-6" style={{ fontFamily: 'Poppins' }} data-testid="shared-cert-name">
              {cert.user_name}
            </h2>

            <p className="text-sm text-slate-500 mb-2">for successfully completing</p>
            <h3 className="text-xl md:text-2xl font-semibold text-slate-900 mb-8" style={{ fontFamily: 'Poppins' }} data-testid="shared-cert-program">
              {cert.program_title_en}
            </h3>

            <div className="flex items-center justify-center gap-8 mb-6">
              <div className="text-center">
                <div className="flex items-center gap-1.5 justify-center mb-1">
                  <Clock className="w-4 h-4 text-blue-500" />
                  <p className="text-2xl font-bold text-blue-600" style={{ fontFamily: 'Poppins' }}>{cert.program_duration}</p>
                </div>
                <p className="text-xs text-slate-400">Duration</p>
              </div>
              <div className="w-px h-12 bg-slate-200" />
              <div className="text-center">
                <div className="flex items-center gap-1.5 justify-center mb-1">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <p className="text-2xl font-bold text-emerald-600" style={{ fontFamily: 'Poppins' }}>{cert.avg_score}%</p>
                </div>
                <p className="text-xs text-slate-400">Score</p>
              </div>
              <div className="w-px h-12 bg-slate-200" />
              <div className="text-center">
                <p className="text-2xl font-bold text-orange-600 mb-1" style={{ fontFamily: 'Poppins' }}>{cert.modules_completed}/{cert.total_modules}</p>
                <p className="text-xs text-slate-400">Modules</p>
              </div>
            </div>

            <Badge style={{ backgroundColor: `${levelColors[cert.program_level]}15`, color: levelColors[cert.program_level], borderColor: `${levelColors[cert.program_level]}40` }}>
              {cert.program_level.charAt(0).toUpperCase() + cert.program_level.slice(1)} Level
            </Badge>

            <div className="mt-8 pt-6 border-t border-slate-100 flex items-center justify-between text-sm">
              <span className="text-slate-400">{formattedDate}</span>
              <span className="font-mono text-blue-600">{cert.verification_code}</span>
            </div>
          </div>
        </div>

        {/* Social proof / CTA */}
        <div className="mt-8 text-center">
          <p className="text-slate-500 mb-4">Want to earn your own AI certificate?</p>
          <Button className="bg-blue-600 hover:bg-blue-700 text-white gap-2" onClick={() => window.location.href = '/auth?mode=register'} data-testid="signup-cta-btn">
            Start Learning Now <ExternalLink className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
